import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { membershipPaymentAPI } from '../../../services/membershipApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';

const SLIP_MAX = 5 * 1024 * 1024;

/**
 * Public page from Finance's "Request re-upload" email/SMS: the member uploads their bank slip
 * again. One-time link (controllers/receiptReuploadController.js); no payment is taken here.
 */
const PublicReceiptReupload = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    membershipPaymentAPI.getReuploadByToken(token)
      .then((res) => setInfo(res.data?.data || null))
      .catch((err) => setError(err.response?.data?.message || 'This link is not valid.'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!file) return setFormError('Attach your bank slip (photo or PDF).');
    if (file.size > SLIP_MAX) return setFormError('The slip must be 5 MB or smaller.');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('receipt', file);
      const res = await membershipPaymentAPI.submitReupload(token, fd);
      setDone(res.data?.message || 'Thank you. Your slip has been uploaded.');
    } catch (err) {
      setFormError(err.response?.data?.message || 'Could not upload your slip. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading" />;

  const shell = (children) => (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-violet-50 to-indigo-100 p-4">
      <div className="w-full max-w-md rounded-xl border border-violet-100 bg-white p-8 shadow-xl">{children}</div>
    </div>
  );

  if (error) {
    return shell(
      <div className="text-center">
        <ExclamationCircleIcon className="mx-auto mb-4 h-14 w-14 text-red-500" />
        <h1 className="mb-2 text-xl font-medium text-gray-900">Link not available</h1>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  if (done) {
    return shell(
      <div className="text-center">
        <CheckCircleIcon className="mx-auto mb-4 h-14 w-14 text-emerald-500" />
        <h1 className="mb-2 text-xl font-medium text-gray-900">Slip received</h1>
        <p className="text-gray-600">{done}</p>
      </div>
    );
  }

  return shell(
    <form onSubmit={submit} className="space-y-5">
      <div>
        <h1 className="text-xl font-medium text-gray-900">Upload your payment slip again</h1>
        <p className="mt-1 text-sm text-gray-600">Dear {info?.memberName || 'Member'}, our finance team needs your bank slip again to confirm your payment. You do not need to pay again.</p>
      </div>

      <dl className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-sm">
        {info?.invoiceNumber && (<div><dt className="text-xs text-gray-500">Invoice</dt><dd className="font-medium text-gray-900">{info.invoiceNumber}</dd></div>)}
        {info?.amount != null && (<div><dt className="text-xs text-gray-500">Amount</dt><dd className="font-medium text-gray-900">{info.currency || 'LKR'} {Number(info.amount).toLocaleString()}</dd></div>)}
        {info?.reason && (<div className="col-span-2"><dt className="text-xs text-gray-500">Reason</dt><dd className="text-gray-800">{info.reason}</dd></div>)}
      </dl>

      <ProofAttachmentDropzone file={file} onChange={setFile} required hint="Photo or PDF of the bank slip, up to 5 MB" />

      {formError && <p className="text-sm text-red-600" role="alert">{formError}</p>}

      <button type="submit" disabled={submitting} className="w-full rounded-xl bg-violet-600 px-6 py-3 font-medium text-white shadow-lg shadow-violet-200 transition-all hover:bg-violet-700 disabled:bg-violet-300">
        {submitting ? 'Uploading' : 'Upload slip'}
      </button>
      {info?.expiresAt && <p className="text-center text-xs text-gray-500">This link is valid until {new Date(info.expiresAt).toLocaleDateString()} and can be used once.</p>}
    </form>
  );
};

export default PublicReceiptReupload;
